import { OtpVerificationError } from '$lib/types/Error';

import translations from './translations';
import { sanitizeValues } from '../_utils/helpers';

import type { FormValues } from '$lib/types/Form';
import type { Language } from '$lib/types/Language';
import type { OtpVerificationParams } from '$lib/types/OtpVerification';

export type OtpSubmitVerificationResult =
    | { ok: true }
    // errorMessage is null when the user already received feedback
    // (e.g. failed or closed the verification modal)
    | { ok: false; errorMessage: string | null };

/**
 * FUN-3876: Shared OTP gate for calendar form submits (Cal.com, Calendly,
 * GoHighLevel, HubSpot, iClosed). Must run before any tracking or navigation so
 * that no contact data is sent and no conversion can fire for leads that fail
 * verification. Returns a localized retry hint when verification could not be
 * started at all (the user never saw the modal).
 */
export const verifyOtpForSubmit = async (
    otpVerificationParams: OtpVerificationParams | null,
    formData: FormValues,
    language: Language,
    providerName: string,
): Promise<OtpSubmitVerificationResult> => {
    const { otpVerification, isNewPhoneField, isPhoneVerificationCodeField } =
        otpVerificationParams || {};

    if (!otpVerification) {
        return { ok: true };
    }

    try {
        const sanitizedValues = sanitizeValues(
            formData,
            isNewPhoneField,
            isPhoneVerificationCodeField,
        );

        await otpVerification(sanitizedValues);

        return { ok: true };
    } catch (error) {
        console.warn(`OTP verification for ${providerName} form failed`, { error });

        if (error instanceof OtpVerificationError && error.reason === 'unavailable') {
            return {
                ok: false,
                errorMessage: translations[language].otpUnavailable[error.channel ?? 'phone'],
            };
        }

        return { ok: false, errorMessage: null };
    }
};
